import { useState, useCallback, useMemo } from 'react';

/**
 * Custom hook for managing multi-select functionality across table views
 * Handles selection state, select all, and cross-page selection
 *
 * @param {Array} items - Current page items
 * @param {number} totalCount - Total count of all items across all pages
 * @param {string} idField - Field name to use as unique identifier (default: 'id')
 * @returns {Object} Selection state and handlers
 */
export const useMultiSelect = (items = [], totalCount = 0, idField = 'id') => {
  // Store selected IDs
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Track if "select all" across all pages is active
  const [selectAllPages, setSelectAllPages] = useState(false);
  // Track explicitly deselected IDs when selectAllPages is true
  const [deselectedIds, setDeselectedIds] = useState(new Set());

  /**
   * Toggle selection for a single item
   */
  const toggleSelect = useCallback(
    (item) => {
      const id = item[idField];

      if (selectAllPages) {
        // When select all is active, toggle deselection
        setDeselectedIds((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(id)) {
            newSet.delete(id);
          } else {
            newSet.add(id);
          }
          return newSet;
        });
      } else {
        // Normal selection toggle
        setSelectedIds((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(id)) {
            newSet.delete(id);
          } else {
            newSet.add(id);
          }
          return newSet;
        });
      }
    },
    [idField, selectAllPages],
  );

  /**
   * Check if an item is selected
   */
  const isSelected = useCallback(
    (item) => {
      const id = item[idField];
      if (selectAllPages) {
        return !deselectedIds.has(id);
      }
      return selectedIds.has(id);
    },
    [idField, selectAllPages, selectedIds, deselectedIds],
  );

  /**
   * Toggle select all on current page
   */
  const toggleSelectAll = useCallback(() => {
    if (selectAllPages) {
      // If all pages selected, check if current page is fully deselected
      const currentPageIds = items.map((item) => item[idField]);
      const allCurrentDeselected = currentPageIds.every((id) => deselectedIds.has(id));

      if (allCurrentDeselected) {
        // If current page is fully deselected, reselect it by removing from deselectedIds
        setDeselectedIds((prev) => {
          const newSet = new Set(prev);
          currentPageIds.forEach((id) => newSet.delete(id));
          return newSet;
        });
      } else {
        // Deselect current page items
        setDeselectedIds((prev) => {
          const newSet = new Set(prev);
          currentPageIds.forEach((id) => newSet.add(id));
          return newSet;
        });
      }
    } else {
      const currentPageIds = items.map((item) => item[idField]);
      const allCurrentSelected = currentPageIds.every((id) => selectedIds.has(id));

      if (allCurrentSelected && selectedIds.size === currentPageIds.length) {
        // If all current page items are selected and nothing else, clear selection
        setSelectedIds(new Set());
      } else {
        // Select all items on current page
        setSelectedIds(new Set(currentPageIds));
      }
    }
  }, [items, idField, selectedIds, selectAllPages, deselectedIds]);

  /**
   * Select all items across all pages
   */
  const selectAllAcrossPages = useCallback(() => {
    setSelectAllPages(true);
    setSelectedIds(new Set());
    setDeselectedIds(new Set());
  }, []);

  /**
   * Remove specific item from selection (useful when item is deleted)
   */
  const removeFromSelection = useCallback(
    (itemOrId) => {
      const id = typeof itemOrId === 'object' ? itemOrId[idField] : itemOrId;

      if (selectAllPages) {
        // When select all is active, add to deselected list
        setDeselectedIds((prev) => {
          const newSet = new Set(prev);
          newSet.add(id);
          return newSet;
        });
      } else {
        // Remove from selected list
        setSelectedIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }
    },
    [idField, selectAllPages],
  );

  /**
   * Clear all selections
   */
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setDeselectedIds(new Set());
    setSelectAllPages(false);
  }, []);

  /**
   * Get count of selected items
   */
  const selectedCount = useMemo(() => {
    if (selectAllPages) {
      return totalCount - deselectedIds.size;
    }
    return selectedIds.size;
  }, [selectAllPages, totalCount, selectedIds.size, deselectedIds.size]);

  /**
   * Check if all items on current page are selected
   */
  const isAllCurrentPageSelected = useMemo(() => {
    if (items.length === 0) return false;
    return items.every((item) => isSelected(item));
  }, [items, isSelected]);

  /**
   * Check if some (but not all) items on current page are selected
   */
  const isSomeCurrentPageSelected = useMemo(() => {
    if (items.length === 0) return false;
    const selectedOnPage = items.filter((item) => isSelected(item)).length;
    return selectedOnPage > 0 && selectedOnPage < items.length;
  }, [items, isSelected]);

  /**
   * Get selection data for API call
   * Returns either explicit IDs or selectAll flag with deselected IDs
   */
  const getSelectionData = useCallback(() => {
    if (selectAllPages) {
      return {
        selectAll: true,
        deselectedIds: Array.from(deselectedIds),
      };
    }
    return {
      selectAll: false,
      ids: Array.from(selectedIds),
    };
  }, [selectAllPages, selectedIds, deselectedIds]);

  return {
    selectedIds: Array.from(selectedIds),
    selectedCount,
    selectAllPages,
    isSelected,
    toggleSelect,
    toggleSelectAll,
    selectAllAcrossPages,
    clearSelection,
    removeFromSelection,
    isAllCurrentPageSelected,
    isSomeCurrentPageSelected,
    getSelectionData,
  };
};
